// services/authApi.ts — HTTP client for /api/auth/* and /api/save.
// Endpoints, request shapes and response shapes match AUTH_CONTRACT.md exactly.
// Authed endpoints go through authedFetch (handles bearer + 401 refresh).

import {
  authedFetch,
  setAuth,
  setTokens,
  getAccessToken,
  API_BASE,
  type AuthUser,
} from "../auth/tokens";
import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from "@simplewebauthn/browser";
import type { GameState } from "../engine";

// ── Common types ────────────────────────────────────────────────────────

export interface RegistrationOptionsBundle {
  challengeId: string;
  options: PublicKeyCredentialCreationOptionsJSON;
}

export interface AuthenticationOptionsBundle {
  challengeId: string;
  options: PublicKeyCredentialRequestOptionsJSON;
}

export interface EmailVerifyResultExisting {
  isNewUser: false;
  hasPasskeys: boolean;
  token: string;
  refreshToken: string;
  user: AuthUser;
  /** Present when hasPasskeys === false — caller MUST run registration ceremony. */
  recoveryRegistration?: RegistrationOptionsBundle;
}

export interface EmailVerifyResultNew {
  isNewUser: true;
  /** Short-lived setup token; pass it back to setupAccount. */
  token: string;
  /** Always null for new users — refresh tokens are minted at setupAccount, not before. */
  refreshToken: null;
}

export type EmailVerifyResult = EmailVerifyResultExisting | EmailVerifyResultNew;

export interface SetupAccountResult {
  user: AuthUser;
  token: string;
  refreshToken: string;
  registration: RegistrationOptionsBundle;
}

export interface PasskeyAuthVerifyResult {
  token: string;
  refreshToken: string;
  user: AuthUser;
}

export interface PasskeyRegisterVerifyResult {
  ok: true;
  credentialId: string;
}

export interface SaveResponse {
  state: GameState | null;
  updated_at: string | null;
}

export interface PutSaveResponse {
  ok: true;
  updated_at: string;
}

// ── Helper: defensively coerce a server-side user blob into AuthUser ────
//
// Every server response that carries a `user` object hands it to this
// normaliser before flowing it into App state. Anything missing /
// wrong-typed coerces to a safe default so we tolerate running briefly
// against an older server during a deploy window — most notably,
// `isAdmin` defaults to false (admin-only surfaces stay hidden) and
// `language` / `foreverDefaultScenarioId` default to null.

function normalizeAuthUser(raw: unknown): AuthUser {
  const u = (raw as Partial<AuthUser> | null) ?? {};
  return {
    id: typeof u.id === "string" ? u.id : "",
    email: typeof u.email === "string" ? u.email : "",
    name: typeof u.name === "string" ? u.name : "",
    foreverDefaultScenarioId:
      typeof u.foreverDefaultScenarioId === "string"
        ? u.foreverDefaultScenarioId
        : null,
    language: typeof u.language === "string" ? u.language : null,
    isAdmin: typeof u.isAdmin === "boolean" ? u.isAdmin : false,
    passkeyCount: typeof u.passkeyCount === "number" ? u.passkeyCount : 0,
    emailLoginCount: typeof u.emailLoginCount === "number" ? u.emailLoginCount : 0,
  };
}

// ── Helper: parse JSON; throw an Error from {error,code} bodies ─────────

async function parseJsonOrThrow<T>(res: Response): Promise<T> {
  let body: unknown = null;
  const text = await res.text();
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      // Non-JSON body — fall through.
    }
  }
  if (!res.ok) {
    const errBody = (body as { error?: string; code?: string }) || {};
    const msg =
      errBody.error ||
      (text ? text.slice(0, 200) : `Request failed with status ${res.status}`);
    const err = new Error(msg) as Error & { code?: string; status?: number };
    if (errBody.code) err.code = errBody.code;
    err.status = res.status;
    throw err;
  }
  return body as T;
}

// ── Email flow ──────────────────────────────────────────────────────────

export async function emailSend(email: string): Promise<{ codeId: string }> {
  const res = await fetch(`${API_BASE}/api/auth/email/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  return parseJsonOrThrow<{ codeId: string }>(res);
}

export async function emailVerify(
  codeId: string,
  code: string
): Promise<EmailVerifyResult> {
  const res = await fetch(`${API_BASE}/api/auth/email/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ codeId, code }),
  });
  const body = await parseJsonOrThrow<EmailVerifyResult>(res);
  // New-user branch has no `user` field; existing-user branches do —
  // route those through the normaliser so isAdmin and the rest of the
  // shape are guaranteed regardless of server version.
  if ("user" in body && body.user) {
    body.user = normalizeAuthUser(body.user);
  }
  return body;
}

// ── Setup (new users) ───────────────────────────────────────────────────
// Uses the short-lived setup token from emailVerify. We send it as a bearer
// directly rather than via authedFetch (no refresh path for setup tokens).

export async function setupAccount(
  setupToken: string,
  name: string,
  scenarioId: string
): Promise<SetupAccountResult> {
  // scenarioId is REQUIRED on the client even though the server treats the
  // field as optional (back-compat for old web bundles). Login.tsx always
  // knows the pill selection — there's no reason to call setupAccount
  // without it from the live app. Sending it pins both the first story's
  // scenarioId and users.forever_default_scenario_id in one server tx.
  // See AUTH_CONTRACT.md POST /api/auth/setup-account.
  const res = await fetch(`${API_BASE}/api/auth/setup-account`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${setupToken}`,
    },
    body: JSON.stringify({ name, scenarioId }),
  });
  const body = await parseJsonOrThrow<SetupAccountResult>(res);
  body.user = normalizeAuthUser(body.user);
  return body;
}

// ── Passkey ─────────────────────────────────────────────────────────────

export async function passkeyAuthenticateOptions(): Promise<AuthenticationOptionsBundle> {
  const res = await fetch(`${API_BASE}/api/auth/passkey/options`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mode: "authenticate" }),
  });
  return parseJsonOrThrow<AuthenticationOptionsBundle>(res);
}

/**
 * Register-mode options. Requires bearer auth (per AUTH_CONTRACT).
 * The new-user signup flow gets options bundled into setupAccount's response,
 * so this is mainly for "add a passkey while logged in" or retry after a
 * failed ceremony (the original challenge is single-use). Pass an explicit
 * bearer token for pre-commitAuth contexts where localStorage doesn't yet
 * hold the session token.
 */
export async function passkeyRegisterOptions(
  bearer?: string,
): Promise<RegistrationOptionsBundle> {
  if (bearer) {
    const res = await fetch(`${API_BASE}/api/auth/passkey/options`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearer}`,
      },
      body: JSON.stringify({ mode: "register" }),
    });
    return parseJsonOrThrow<RegistrationOptionsBundle>(res);
  }
  const res = await authedFetch(`/api/auth/passkey/options`, {
    method: "POST",
    body: JSON.stringify({ mode: "register" }),
  });
  return parseJsonOrThrow<RegistrationOptionsBundle>(res);
}

export async function passkeyVerifyAuthenticate(
  challengeId: string,
  response: AuthenticationResponseJSON
): Promise<PasskeyAuthVerifyResult> {
  const res = await fetch(`${API_BASE}/api/auth/passkey/verify`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ challengeId, response, mode: "authenticate" }),
  });
  const body = await parseJsonOrThrow<PasskeyAuthVerifyResult>(res);
  body.user = normalizeAuthUser(body.user);
  return body;
}

/**
 * Register-mode verify is authed: the caller already holds a session token
 * (either from setup-account or from the email/verify recovery path).
 */
export async function passkeyVerifyRegister(
  challengeId: string,
  response: RegistrationResponseJSON,
  bearer?: string
): Promise<PasskeyRegisterVerifyResult> {
  // If a specific bearer is provided (the setup-account session token, or
  // the recovery session token), use it directly so we don't accidentally
  // pick up some stale token from localStorage. Falls back to authedFetch.
  if (bearer) {
    const res = await fetch(`${API_BASE}/api/auth/passkey/verify`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearer}`,
      },
      body: JSON.stringify({ challengeId, response, mode: "register" }),
    });
    return parseJsonOrThrow<PasskeyRegisterVerifyResult>(res);
  }
  const res = await authedFetch(`/api/auth/passkey/verify`, {
    method: "POST",
    body: JSON.stringify({ challengeId, response, mode: "register" }),
  });
  return parseJsonOrThrow<PasskeyRegisterVerifyResult>(res);
}

// ── Account / language (Phase A i18n) ───────────────────────────────────

/**
 * GET /api/auth/me — returns the current user's publicUser shape so the
 * client can hydrate fields that weren't in the JWT claim (today:
 * foreverDefaultScenarioId and language). Authed.
 */
export async function getMe(): Promise<{ user: AuthUser }> {
  const res = await authedFetch(`/api/auth/me`, { method: "GET" });
  const body = await parseJsonOrThrow<{ user: AuthUser }>(res);
  return { user: normalizeAuthUser(body.user) };
}

/**
 * PUT /api/auth/language — set the user's account-level narration
 * language. `code` must be a BCP-47 code from SUPPORTED_CODES; the server
 * 400s `unsupported_language` otherwise. Returns the persisted code on
 * success so the caller can confidently update local state without a
 * follow-up /me round-trip.
 */
export async function updateLanguage(
  code: string,
): Promise<{ language: string }> {
  const res = await authedFetch(`/api/auth/language`, {
    method: "PUT",
    body: JSON.stringify({ language: code }),
  });
  const body = await parseJsonOrThrow<{ ok: true; language: string }>(res);
  return { language: body.language };
}

// ── Refresh / logout ────────────────────────────────────────────────────

export async function refresh(refreshToken: string): Promise<{ token: string }> {
  const res = await fetch(`${API_BASE}/api/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  return parseJsonOrThrow<{ token: string }>(res);
}

export async function logout(): Promise<void> {
  // Stateless on the server side per AUTH_CONTRACT — just call it best-effort.
  try {
    if (getAccessToken()) {
      await authedFetch(`/api/auth/logout`, { method: "POST" });
    }
  } catch {
    // ignore — logout is local-state-driven
  }
}

// ── Save ────────────────────────────────────────────────────────────────

export async function getSave(): Promise<SaveResponse> {
  const res = await authedFetch(`/api/save`, { method: "GET" });
  return parseJsonOrThrow<SaveResponse>(res);
}

export async function putSave(state: GameState): Promise<PutSaveResponse> {
  const res = await authedFetch(`/api/save`, {
    method: "PUT",
    body: JSON.stringify({ state }),
  });
  return parseJsonOrThrow<PutSaveResponse>(res);
}

// ── OAuth ──────────────────────────────────────────────────────────────

export interface OAuthResult {
  token: string;
  refreshToken: string;
  user: AuthUser;
  isNewUser: boolean;
}

export async function oauthGoogle(
  credential: string,
  scenarioId?: string,
): Promise<OAuthResult> {
  const res = await fetch(`${API_BASE}/api/auth/oauth/google`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ credential, scenarioId }),
  });
  const body = await parseJsonOrThrow<OAuthResult>(res);
  body.user = normalizeAuthUser(body.user);
  return body;
}

export async function oauthApple(
  idToken: string,
  user?: { name?: { firstName?: string; lastName?: string } },
  scenarioId?: string,
): Promise<OAuthResult> {
  const res = await fetch(`${API_BASE}/api/auth/oauth/apple`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id_token: idToken, user, scenarioId }),
  });
  const body = await parseJsonOrThrow<OAuthResult>(res);
  body.user = normalizeAuthUser(body.user);
  return body;
}

// ── Helpers exposed for the Login state machine ─────────────────────────

/**
 * Persist tokens + user after a successful auth completion. Wraps tokens.setAuth
 * for symmetry with the other helpers in this module.
 */
export function commitAuth(token: string, refreshToken: string, user: AuthUser): void {
  setAuth(token, refreshToken, user);
}

/**
 * Persist just the access token (used during setup → register chains where
 * we want the new session token in place before kicking off the next call).
 */
export function commitTokens(token: string, refreshToken?: string): void {
  setTokens(token, refreshToken);
}
